import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import { appConfig } from '@/shared/config/app-config'
import {
  backendToAppLanguage,
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
} from './languages'

const STORAGE_KEY = `${appConfig.storagePrefix}-language`

const urlLocaleRaw = new URLSearchParams(window.location.search).get('locale')
const urlLocale = urlLocaleRaw ? backendToAppLanguage(urlLocaleRaw) : null
const isAutoLogin = window.location.pathname.startsWith('/autologin')
export let localeFromUrl = !!urlLocale && !isAutoLogin
if (urlLocale) {
  localStorage.setItem(STORAGE_KEY, urlLocale)
}
const savedLanguage = localStorage.getItem(STORAGE_KEY)
const browserLanguage = navigator.language.split('-')[0]
const initialLanguage =
  urlLocale ??
  savedLanguage ??
  (SUPPORTED_LANGUAGES.find((l) => l.code === browserLanguage)
    ? browserLanguage
    : DEFAULT_LANGUAGE)

async function loadTranslation(lang: string): Promise<Record<string, string>> {
  const module = await import(`./locales/${lang}/translation.json`)
  return module.default
}

async function loadDisplayFont(lang: string) {
  if (lang !== 'ja') return
  // A failed font request must not reject i18nReady and blank the app.
  await import('./japanese-display-font.css').catch(() => {})
}

i18n.use(initReactI18next).init({
  lng: initialLanguage,
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
  interpolation: { escapeValue: false },
  resources: {},
})

const setHtmlLang = (lng: string) => {
  document.documentElement.lang = lng
}
i18n.on('languageChanged', setHtmlLang)
setHtmlLang(initialLanguage)

// Load initial language + fallback + display font
async function loadInitialTranslations() {
  const [initial, fallback] = await Promise.all([
    loadTranslation(initialLanguage),
    initialLanguage !== DEFAULT_LANGUAGE
      ? loadTranslation(DEFAULT_LANGUAGE)
      : null,
    loadDisplayFont(initialLanguage),
  ])

  i18n.addResourceBundle(initialLanguage, 'translation', initial)
  if (fallback) {
    i18n.addResourceBundle(DEFAULT_LANGUAGE, 'translation', fallback)
  }
  await i18n.changeLanguage(initialLanguage)
}

export const i18nReady = loadInitialTranslations()

export async function changeLanguage(code: string) {
  localeFromUrl = false
  localStorage.setItem(STORAGE_KEY, code)

  if (!i18n.hasResourceBundle(code, 'translation')) {
    const translation = await loadTranslation(code)
    i18n.addResourceBundle(code, 'translation', translation)
  }

  await loadDisplayFont(code)
  return i18n.changeLanguage(code)
}

export function getCurrentLanguage(): string {
  return i18n.language
}

export default i18n
